/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef } from "react";

import algoliasearch from "algoliasearch";
import { Configure, InstantSearch } from "react-instantsearch-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { ModalOverlay } from "src/components/UI/Modal/Modal";
import { ALGOLIA_INDEX_NAME } from "src/lib/config";
import { AppState } from "src/store";
import { searchActions } from "src/store/modules/search";

import { BasicSearchBox } from "./BasicSearchBox";
import { SHORTCUTS } from "./constants";
import { CustomSearchBox } from "./SearchBox";
import { CustomStateResults } from "./SearchPiping";

interface Props {
  algoliaApiKey: string;
}

export function SearchModal({ algoliaApiKey }: Props) {
  const dispatch = useDispatch();

  const backgroundRef = useRef(null);

  const searchModalOpen = useSelector((state: AppState) => state.search.modalOpen); // the type of search modal open
  const searchClient = algoliasearch("T4XM1HXWPD", algoliaApiKey);

  const handleClick = (e: React.MouseEvent) => {
    if (e.target === backgroundRef.current) {
      dispatch(searchActions.searchModalClosed({}));
    }
  };

  return (
    <ModalOverlay ref={backgroundRef} onClick={handleClick} id="cy-search-modal">
      {searchModalOpen === "command" ? (
        <SearchContainer>
          <BasicSearchBox options={SHORTCUTS} />
        </SearchContainer>
      ) : (
        <InstantSearch indexName={`${ALGOLIA_INDEX_NAME}_items`} searchClient={searchClient}>
          <Configure hitsPerPage={9} />
          <SearchContainer>
            <CustomSearchBox />
            <SearchResultsContainer>
              <CustomStateResults />
            </SearchResultsContainer>
          </SearchContainer>
        </InstantSearch>
      )}
    </ModalOverlay>
  );
}

// Container for the whole search modal
export const SearchContainer = styled.div`
  background: #fbfbfb;
  border: 2px solid #000000;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 100%;
  max-height: 832px;
  max-width: 520px;
  overflow: hidden;
  margin-top: 30px;
`;

const SearchResultsContainer = styled.div`
  border: 0 solid rgba(174, 174, 174, 1);
  overflow-y: auto;
`;

export default SearchModal;
